<template>
  <div class="flex min-h-screen bg-[#1F1F1F] text-white">
    <!-- Mobile Menu Overlay -->
    <div 
      v-if="isMobileMenuOpen" 
      @click="closeMobileMenu"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
    ></div>

    <!-- Sidebar -->
    <div 
      :class="[
        'bg-gradient-to-b from-[#2A2A2A] to-[#1F1F1F] p-4 flex flex-col border-r border-[#3A3A3A] shadow-xl transition-all duration-300 z-50',
        'lg:w-64 lg:relative lg:translate-x-0',
        isMobileMenuOpen ? 'fixed inset-y-0 left-0 w-64 translate-x-0' : 'fixed inset-y-0 left-0 w-64 -translate-x-full lg:translate-x-0'
      ]"
    >
      <!-- Mobile Close Button -->
      <button 
        @click="closeMobileMenu"
        class="lg:hidden absolute top-4 right-4 text-gray-400 hover:text-white"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>

      <!-- Logo Section -->
      <div class="flex items-center mb-8 pb-4 border-b border-[#3A3A3A]">
        <img src="../assets/logo.png" alt="Tuscany House Logo" class="w-14 h-14 rounded-full mr-4 shadow-lg" />
        <div class="flex flex-col">
          <span class="font-bold text-lg text-[#F5BE32] tracking-wide">TUSCANY HOUSE</span>
          <div class="flex items-center">
            <div class="mr-2 w-5 h-5 bg-[#F5BE32] rounded-full flex items-center justify-center">
              <span class="text-[#1F1F1F] text-xs font-bold">A</span>
            </div>
            <span class="text-xs text-gray-400">Admin Panel</span>
          </div>
        </div>
      </div>
      
      <!-- Navigation -->
      <nav class="flex-1 space-y-1">
        <div 
          class="flex items-center p-3 rounded-xl cursor-pointer transition-all duration-300 hover:bg-[#F5BE32] hover:bg-opacity-10 group"
          :class="{ 'bg-[#F5BE32] bg-opacity-15 border-l-4 border-[#F5BE32]': $route.path === '/admin' }"
          @click="navigateTo('/admin')"
        >
          <svg class="w-5 h-5 mr-4 transition-colors duration-300" :class="$route.path === '/admin' ? 'text-[#F5BE32]' : 'text-gray-400 group-hover:text-[#F5BE32]'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z"></path>
          </svg>
          <span class="text-base font-medium transition-colors duration-300" :class="$route.path === '/admin' ? 'text-[#F5BE32]' : 'text-gray-300 group-hover:text-white'">
            {{ $t('admin.overview.title') }}
          </span>
        </div>
        
        <div 
          class="flex items-center p-3 rounded-xl cursor-pointer transition-all duration-300 hover:bg-[#F5BE32] hover:bg-opacity-10 group"
          :class="{ 'bg-[#F5BE32] bg-opacity-15 border-l-4 border-[#F5BE32]': $route.path === '/admin/orders' }"
          @click="navigateTo('/admin/orders')"
        >
          <svg class="w-5 h-5 mr-4 transition-colors duration-300" :class="$route.path === '/admin/orders' ? 'text-[#F5BE32]' : 'text-gray-400 group-hover:text-[#F5BE32]'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
          </svg>
          <span class="text-base font-medium transition-colors duration-300" :class="$route.path === '/admin/orders' ? 'text-[#F5BE32]' : 'text-gray-300 group-hover:text-white'">
            {{ $t('admin.orders.title') }}
          </span>
        </div>
        
        <div 
          class="flex items-center p-3 rounded-xl cursor-pointer transition-all duration-300 hover:bg-[#F5BE32] hover:bg-opacity-10 group"
          :class="{ 'bg-[#F5BE32] bg-opacity-15 border-l-4 border-[#F5BE32]': $route.path === '/admin/messages' }"
          @click="navigateTo('/admin/messages')"
        >
          <svg class="w-5 h-5 mr-4 transition-colors duration-300" :class="$route.path === '/admin/messages' ? 'text-[#F5BE32]' : 'text-gray-400 group-hover:text-[#F5BE32]'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
          </svg>
          <span class="text-base font-medium transition-colors duration-300" :class="$route.path === '/admin/messages' ? 'text-[#F5BE32]' : 'text-gray-300 group-hover:text-white'">
            {{ $t('admin.messages.title') }}
          </span>
        </div>
      </nav>
      
      <!-- Logout Section -->
      <div class="border-t border-[#3A3A3A] pt-4">
        <div 
          class="flex items-center p-3 rounded-xl cursor-pointer transition-all duration-300 hover:bg-red-500 hover:bg-opacity-10 group"
          @click="handleLogout"
        >
          <svg class="w-5 h-5 mr-4 text-gray-400 group-hover:text-red-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
          </svg>
          <span class="text-base font-medium text-gray-300 group-hover:text-red-400 transition-colors duration-300">
            {{ $t('admin.common.logout') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 bg-gradient-to-br from-[#1F1F1F] to-[#2A2A2A] overflow-auto">
      <!-- Header with Mobile Menu Toggle and Language Switcher -->
      <div class="flex justify-between items-center p-4 lg:p-6 border-b border-[#3A3A3A]">
        <!-- Mobile Menu Toggle -->
        <button 
          @click="openMobileMenu"
          class="lg:hidden text-white hover:text-[#F5BE32] transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
        </button>

        <h2 class="text-lg lg:text-xl font-semibold text-white">{{ getCurrentPageTitle() }}</h2>
        <LanguageSwitcher @language-changed="handleLanguageChange" />
      </div>
      
      <div class="p-4 lg:p-8">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import LanguageSwitcher from './LanguageSwitcher.vue';

export default {
  name: 'AdminLayout',
  components: {
    LanguageSwitcher
  },
  data() {
    return {
      isMobileMenuOpen: false
    }
  },
  methods: {
    openMobileMenu() {
      this.isMobileMenuOpen = true;
    },
    
    closeMobileMenu() {
      this.isMobileMenuOpen = false;
    },
    
    navigateTo(path) {
      this.$router.push(path);
      this.closeMobileMenu();
    },
    
    handleLogout() {
      this.$router.push('/admin/login')
    },
    
    handleLanguageChange(newLanguage) {
      this.$i18n.locale = newLanguage;
    },
    
    getCurrentPageTitle() {
      const route = this.$route.path;
      if (route === '/admin') {
        return this.$t('admin.overview.title');
      } else if (route === '/admin/orders') {
        return this.$t('admin.orders.title');
      } else if (route === '/admin/messages') {
        return this.$t('admin.messages.title');
      }
      return 'Admin';
    }
  }
}
</script>